import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "@/lib/platform";

interface AuthCtx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

/**
 * Breadcrumb log of auth events, persisted to sessionStorage so it survives
 * the Chrome Custom Tab bounce during native Google sign-in. Read by
 * AuthDiagnostics. No tokens are stored — only event type, timestamp, and a
 * sanitized URL path/hash structure.
 */
export type AuthEventType =
  | "oauth-initiated"
  | "deep-link-received"
  | "callback-implicit"
  | "callback-pkce"
  | "set-session-success"
  | "set-session-failure"
  | "callback-timeout";

export interface AuthBreadcrumb {
  type: AuthEventType;
  at: number; // epoch ms
  detail?: string;
}

const BREADCRUMB_KEY = "fitaura.auth.breadcrumbs";
const MAX_BREADCRUMBS = 25;

export function recordAuthEvent(type: AuthEventType, detail?: string): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.sessionStorage.getItem(BREADCRUMB_KEY);
    const list: AuthBreadcrumb[] = raw ? JSON.parse(raw) : [];
    list.push({ type, at: Date.now(), detail });
    const trimmed = list.slice(-MAX_BREADCRUMBS);
    window.sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(trimmed));
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
}

export function readAuthEvents(): AuthBreadcrumb[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(BREADCRUMB_KEY);
    return raw ? (JSON.parse(raw) as AuthBreadcrumb[]) : [];
  } catch {
    return [];
  }
}

export function clearAuthEvents(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(BREADCRUMB_KEY);
  } catch {
    /* noop */
  }
}

/** Sanitize a URL for breadcrumb storage — drops tokens but keeps shape. */
function sanitizeUrlForBreadcrumb(url: string): string {
  try {
    const u = new URL(url);
    const hashKeys = u.hash
      ? Array.from(new URLSearchParams(u.hash.replace(/^#/, "")).keys()).join(",")
      : "";
    const queryKeys = Array.from(u.searchParams.keys()).join(",");
    return `${u.protocol}//${u.host}${u.pathname}${queryKeys ? `?[${queryKeys}]` : ""}${hashKeys ? `#[${hashKeys}]` : ""}`;
  } catch {
    return url.slice(0, 60);
  }
}

/**
 * Parse tokens from a deep-link callback URL like:
 *   fitauraai://oauth-callback#access_token=...&refresh_token=...
 * or query-form:
 *   fitauraai://oauth-callback?access_token=...&refresh_token=...
 */
function extractTokensFromUrl(url: string): { access_token: string; refresh_token: string } | null {
  try {
    const u = new URL(url);
    // Try hash fragment first (Supabase implicit-flow style).
    const hash = u.hash.startsWith("#") ? u.hash.slice(1) : u.hash;
    const hashParams = new URLSearchParams(hash);
    const queryParams = u.searchParams;
    const access_token = hashParams.get("access_token") ?? queryParams.get("access_token");
    const refresh_token = hashParams.get("refresh_token") ?? queryParams.get("refresh_token");
    if (access_token && refresh_token) return { access_token, refresh_token };
    return null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setLoading(false);
      if (event === "SIGNED_IN" && s?.user) {
        setTimeout(() => {
          import("@/lib/native-push")
            .then(({ syncPushTokenAfterAuth }) => syncPushTokenAfterAuth())
            .catch((err) => console.warn("[push] sync import failed:", err));
          // Self-heal any Play purchases that succeeded on Google's side but
          // never got verified/consumed (e.g. earlier 401 / network drop).
          import("@/lib/native-billing")
            .then(({ restoreStuckPurchases }) => restoreStuckPurchases())
            .then((r) => {
              if (r.restored > 0) {
                console.log("[billing] restored stuck purchases", r);
              }
            })
            .catch((err) => console.warn("[billing] restore failed:", err));
        }, 0);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session?.user) {
        import("@/lib/native-push")
          .then(({ syncPushTokenAfterAuth }) => syncPushTokenAfterAuth())
          .catch((err) => console.warn("[push] initial sync import failed:", err));
        import("@/lib/native-billing")
          .then(({ restoreStuckPurchases }) => restoreStuckPurchases())
          .then((r) => {
            if (r.restored > 0) console.log("[billing] restored stuck purchases", r);
          })
          .catch((err) => console.warn("[billing] restore failed:", err));
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Native deep-link OAuth callback handler — LEGACY FALLBACK ONLY.
  // The primary native sign-in flow now uses the Google Play Services account
  // picker (see Auth.tsx → @codetrix-studio/capacitor-google-auth) and never
  // opens a Chrome Custom Tab. This listener is kept as a safety net for:
  //   1. Users mid-flow on an older app version during rollout.
  //   2. Any future cross-app handoff that lands on `fitauraai://`.
  // It can be removed after a few release cycles once telemetry shows zero
  // `deep-link-received` events.
  useEffect(() => {
    if (!isNativeApp()) return;

    let appHandle: { remove: () => Promise<void> } | undefined;

    (async () => {
      try {
        const [{ App: CapApp }, { Browser }] = await Promise.all([
          import("@capacitor/app"),
          import("@capacitor/browser"),
        ]);

        appHandle = await CapApp.addListener("appUrlOpen", async ({ url }) => {
          if (!url || !url.startsWith("fitauraai://")) return;
          recordAuthEvent("deep-link-received", sanitizeUrlForBreadcrumb(url));
          const tokens = extractTokensFromUrl(url);
          if (!tokens) return;

          try {
            await supabase.auth.setSession({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
            });
            recordAuthEvent("set-session-success", "deep-link");
          } catch (e) {
            console.error("[auth] setSession from deep link failed", e);
            recordAuthEvent(
              "set-session-failure",
              e instanceof Error ? e.message : "deep-link",
            );
          }

          // Close the in-app Chrome Custom Tab so the user lands back in the
          // app immediately instead of seeing the OAuth provider's redirect page.
          try {
            await Browser.close();
          } catch {
            /* browser already closed — non-fatal */
          }
        });
      } catch (e) {
        console.warn("[auth] deep-link listener could not be installed", e);
      }
    })();

    return () => {
      appHandle?.remove().catch(() => undefined);
    };
  }, []);

  return (
    <Ctx.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        signOut: async () => {
          // Remove this device's FCM token before signing out so pushes
          // stop reaching it. Best-effort; never blocks sign-out.
          try {
            const { cleanupPushOnSignOut } = await import("@/lib/native-push");
            await cleanupPushOnSignOut();
          } catch {
            /* non-fatal */
          }
          await supabase.auth.signOut();
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
